import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, ValidatorFn, FormsModule } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { UserService } from '../../services/user.service';
import { ContactService } from '../../services/contact.service';
import { RegistrationService } from '../../services/registration.service';
import { ReferenceService } from '../../services/reference.service';

import { SecurityQuestionsListItem } from '../../models/registration';
import {
  ContactInformation,
  GetContactInformation,
  UserNamePassword,
  SecurityQuestionChange,
  UserNamePasswordChange
} from '../../models/contact';
import { CompanyService } from 'src/app/services/company.service';
import { StatesListItem, CountryListItem, VerifyListItem } from '../../models/reference';
@Component({
  selector: 'app-contact-information',
  templateUrl: './contact-information.component.html',
  styleUrls: ['./contact-information.component.scss']
})

export class ContactInformationComponent {
  private _handleError: HandleError;
  private _logging = environment.logging;
  fieldValidations = environment.fieldValidations;

  // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showSpinner = false;
  showSaveContactInformationMessageSpinner = false;
  showSavePasswordMessageSpinner = false;
  showSaveSecurityQuestionMessageSpinner = false;
  showContactSaveSuccess = false;
  showContactSaveError = false;
  showPasswordSaveSuccess = false;
  showPasswordSaveError = false;
  showSecurityQuestionSaveSuccess = false;
  showSecurityQuestionSaveError = false;
  allowContactInfoEdit = false;
  highlightFields = false;
  allowPasswordEdit = false;
  allowSecurityQuestionEdit = false;
  confirmPasswordInputClicked = false;
  userId: number = null;
  getStateListComplete = false;
  stateList: StatesListItem[] = [];
  showStateListSpinner = false;
  getCountryListComplete = false;
  countryList: CountryListItem[] = [];
  showCountryListSpinner = false;
  verifySt_CntyListComplete = false;
  verifyst_cntry: VerifyListItem = null;
  status!: 'VL' | 'IN';

  contactInformation: ContactInformation = null;
  contactInformationFound = false;
  getContactInformation: GetContactInformation = null;
  userNamePassword: UserNamePassword = null;
  userNamePasswordFound = false;
  securityQuestionChangeSend: SecurityQuestionChange = null;
  securityQuestionChangeReturn: SecurityQuestionChange = null;
  securityQuestionChangeFound = false;
  securityQuestionsList: SecurityQuestionsListItem[] = [];
  securityQuestionListFound = false;
  userNamePasswordChange: UserNamePasswordChange = null;

  contactInfoForm: UntypedFormGroup;
  securityInfoForm: UntypedFormGroup;
  firstNameFormControl = new UntypedFormControl();
  middleInitialFormControl = new UntypedFormControl();
  lastNameFormControl = new UntypedFormControl();
  addressLine1FormControl = new UntypedFormControl();
  addressLine2FormControl = new UntypedFormControl();
  cityFormControl = new UntypedFormControl();
  stateFormControl = new UntypedFormControl();
  zipCodeFormControl = new UntypedFormControl();
  countryFormControl = new UntypedFormControl();
  homePhoneFormControl = new UntypedFormControl();
  cellPhoneFormControl = new UntypedFormControl();
  workPhoneFormControl = new UntypedFormControl();
  homeEmailFormControl = new UntypedFormControl();

  userNameFormControl = new UntypedFormControl();
  passwordFormControl = new UntypedFormControl();
  passwordConfirmFormControl = new UntypedFormControl();
  userPasswordEqualsFormControl = new UntypedFormControl();
  securityQuestionFormControl = new UntypedFormControl();
  securityQuestionIdFormControl = new UntypedFormControl();
  securityAnswerFormControl = new UntypedFormControl();

  constructor(
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _userService: UserService,
    private _contactService: ContactService,
    private _registrationService: RegistrationService,
    private _referenceService: ReferenceService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('ContactInformationComponent');
    this.getData();
    this.scrollToTop();
  }

  getValidInvalidClass() {
  return {
    'field-invalid': this.status === 'IN',
    'field-valid': this.status === 'VL'
  };
}

  getData(): void {
    this.showSpinner = true;
    this.getStateListComplete = false;
    this.getCountryListComplete = false;
    const passwordMaskConstant = '*******';
    const participantId: number = this._userService.getParticipantId();
    const userName: string = this._userService.getUserName();
    this.getContactInformation = {
      userName: userName,
      participantId: participantId
    };

    this.userNamePassword = {
      userName: userName,
      password: passwordMaskConstant
    };
    this.securityQuestionChangeSend = {
      userName: userName,
      userId: null,
      securityQuestionId: null,
      securityQuestionText: null,
      securityQuestionAnswer: null,
      participantId: null
    };

    this.getStateList();
    this.getCountryList();
    this.getContactInformationData(this.getContactInformation);
    this.getUserNamePasswordData();
    this.getSecurityQuestionData(this.securityQuestionChangeSend);
    this.getSecurityQuestionsList();
  }

  getContactInformationData(getContactInformation: GetContactInformation): void {
    this.showSpinner = true;
    this.contactInformationFound = false;
    this._contactService.getContactInformation(getContactInformation).subscribe(
      result => {
        this.contactInformationFound = result !== null && result.userId !== null && result.userId > 0;
        this.contactInformation = result;

        if (this._logging.serviceCallTraceLog) {
          console.log('ContactInformationComponent.getContactInformationData :: result.userId = ' + result.userId);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ContactInformationComponent.getContactInformationData :: result = ' + JSON.stringify(result));
        }

        this.getDataComplete();
      },
      error => this._handleError('getContactInformationData', error)
    );
  }

  getStateList(): void {
    this.showSpinner = true;
    this.getStateListComplete = false;
    this.stateList = [];

    this._referenceService.getStatesList().subscribe(
      resultArray => {
        this.stateList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('ContactInformationComponent.getStateList :: resultArray.length= ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ContactInformationComponent.getStateList :: resultArray = ' + JSON.stringify(resultArray));
        }
        this.getStateListComplete = true;
      },
      error => this._handleError('getStateList', error)
    );
  }

  // 03/16/2021 RA Get State list filtered on Country Code
  getStateCountryList(country: string = null): void {
    this.getStateListComplete = false;
    this.stateList = [];

    this._referenceService.getStatesCountryList(country).subscribe(
      resultArray => {
        this.stateList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('ContactInformationComponent.getStateCountryList :: resultArray.length= ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ContactInformationComponent.getStateCountryList :: resultArray = ' + JSON.stringify(resultArray));
        }
        this.getStateListComplete = true;
      },
      error => this._handleError('getStateCountryList', error)
    );
  }

  getCountryList(): void {
    this.showSpinner = true;
    this.getCountryListComplete = false;
    this.countryList = [];

    this._referenceService.getCountryList().subscribe(
      resultArray => {
        this.countryList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('ContactInformationComponent.getCountryList :: resultArray.length= ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ContactInformationComponent.getCountryList :: resultArray = ' + JSON.stringify(resultArray));
        }
        this.getCountryListComplete = true;
      },
      error => this._handleError('getCountryList', error)
    );
  }

  // 04/01/2021 RA Verify Country Code for State provided
  verifyState_Country(state: string = null, country: string = null): void {
    this.verifySt_CntyListComplete = false;

    this._referenceService.verifyStateCountry(state, country).subscribe(
      result => {
        this.verifySt_CntyListComplete = result !== null && result.countryCode != null;
        this.verifyst_cntry = result;
        // 04/13/2021 RA - To null out the state field if it's not valid
        if (this.verifySt_CntyListComplete === false) {
          this.contactInfoForm.get('stateFormControl').setValue(null);
        }
        if (this._logging.serviceCallTraceLog) {
          console.log('ContactInformationComponent.verifyState_Country :: resultArray.length= ' + result);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ContactInformationComponent.verifyState_Country :: resultArray = ' + JSON.stringify(result));
        }
      },
      error => this._handleError('verifyState_Country', error)
    );
  }

  getUserNamePasswordData(): void {
    this.showSpinner = true;
    this.userNamePasswordFound = true;
    this.getDataComplete();
  }

  getSecurityQuestionData(securityQuestionChangeSend: SecurityQuestionChange): void {
    this.showSpinner = true;
    this.securityQuestionChangeFound = false;
    this._contactService.getSecurityQuestionData(securityQuestionChangeSend).subscribe(
      result => {
        this.securityQuestionChangeFound = result !== null && result.userId !== null && result.userId > 0;
        this.securityQuestionChangeReturn = result;

        if (this._logging.serviceCallTraceLog) {
          console.log('ContactInformationComponent.getContactInformationData :: result.userId = ' + result.userId);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ContactInformationComponent.getContactInformationData :: result = ' + JSON.stringify(result));
        }

        this.getDataComplete();
      },
      error => this._handleError('getContactInformationData', error)
    );
  }

  getSecurityQuestionsList(): void {
    this.showSpinner = true;
    this.securityQuestionListFound = false;
    this._registrationService.getSecurityQuestionsList().subscribe(
      resultArray => {
        this.securityQuestionListFound = resultArray !== null && resultArray.length > 0;
        this.securityQuestionsList = resultArray;
        if (this._logging.serviceCallTraceLog) {
          console.log('ContactInformationComponent.getSecurityQuestionsList :: result.length = ' + resultArray.length);
        }
        if (this._logging.serviceDataTraceLog) {
          console.log('ContactInformationComponent.getSecurityQuestionsList :: result = ' + JSON.stringify(resultArray));
        }

        this.getDataComplete();
      },
      error => this._handleError('getSecurityQuestionsList', error)
    );
  }

  getDataComplete(): void {
    if (this.contactInformationFound && this.userNamePasswordFound && this.securityQuestionChangeFound && this.securityQuestionListFound) {
      this.showSpinner = false;
      this.createForm();
    }
  }

  createForm(): void {
    this.contactInfoForm = this._formBuilder.group({});
    this.contactInfoForm.addControl('firstNameFormControl', this.firstNameFormControl);
    this.contactInfoForm.addControl('middleInitialFormControl', this.middleInitialFormControl);
    this.contactInfoForm.addControl('lastNameFormControl', this.lastNameFormControl);
    this.contactInfoForm.addControl('addressLine1FormControl', this.addressLine1FormControl);
    this.contactInfoForm.addControl('addressLine2FormControl', this.addressLine2FormControl);
    this.contactInfoForm.addControl('cityFormControl', this.cityFormControl);
    this.contactInfoForm.addControl('stateFormControl', this.stateFormControl);
    this.contactInfoForm.addControl('zipCodeFormControl', this.zipCodeFormControl);
    this.contactInfoForm.addControl('countryFormControl', this.countryFormControl);
    this.contactInfoForm.addControl('homePhoneFormControl', this.homePhoneFormControl);
    this.contactInfoForm.addControl('cellPhoneFormControl', this.cellPhoneFormControl);
    this.contactInfoForm.addControl('workPhoneFormControl', this.workPhoneFormControl);
    this.contactInfoForm.addControl('homeEmailFormControl', this.homeEmailFormControl);

    this.securityInfoForm = this._formBuilder.group({});
    this.securityInfoForm.addControl('userNameFormControl', this.userNameFormControl);
    this.securityInfoForm.addControl('passwordFormControl', this.passwordFormControl);
    this.securityInfoForm.addControl('passwordConfirmFormControl', this.passwordConfirmFormControl);
    this.securityInfoForm.addControl('userPasswordEqualsFormControl', this.userPasswordEqualsFormControl);
    this.securityInfoForm.addControl('securityQuestionFormControl', this.securityQuestionFormControl);
    this.securityInfoForm.addControl('securityAnswerFormControl', this.securityAnswerFormControl);
    this.securityInfoForm.addControl('securityQuestionIdFormControl', this.securityQuestionIdFormControl);

    this.contactInfoForm.setValue({
      firstNameFormControl: this.contactInformation.firstName,
      middleInitialFormControl: this.contactInformation.middleInitial,
      lastNameFormControl: this.contactInformation.lastName,
      addressLine1FormControl: this.contactInformation.addressLine1,
      addressLine2FormControl: this.contactInformation.addressLine2,
      cityFormControl: this.contactInformation.city,
      countryFormControl: this.contactInformation.country,
      stateFormControl: this.contactInformation.state,
      zipCodeFormControl: this.contactInformation.zipCode,
      homePhoneFormControl: this.contactInformation.homePhone,
      cellPhoneFormControl: this.contactInformation.cellPhone,
      workPhoneFormControl: this.contactInformation.workPhone,
      homeEmailFormControl: this.contactInformation.homeEmail
    });

    this.userId = this.contactInformation.userId;

    this.securityInfoForm.setValue({
      userNameFormControl: this.userNamePassword.userName,
      passwordFormControl: this.userNamePassword.password,
      passwordConfirmFormControl: this.userNamePassword.password,
      userPasswordEqualsFormControl: true,
      securityQuestionFormControl: this.securityQuestionChangeReturn.securityQuestionText,
      securityAnswerFormControl: this.securityQuestionChangeReturn.securityQuestionAnswer,
      securityQuestionIdFormControl: this.securityQuestionChangeReturn.securityQuestionId.toString()
    });

    this.contactInfoForm.get('firstNameFormControl').setValidators([Validators.required]);
    this.contactInfoForm.get('middleInitialFormControl');
    this.contactInfoForm.get('lastNameFormControl').setValidators([Validators.required]);
    this.contactInfoForm.get('addressLine1FormControl').setValidators([Validators.required]);
    this.contactInfoForm.get('addressLine2FormControl');
    this.contactInfoForm.get('cityFormControl').setValidators([Validators.required]);
    this.contactInfoForm.get('stateFormControl').setValidators([Validators.required]);
    this.contactInfoForm.get('countryFormControl').setValidators([Validators.required]);              // 04/07/2021 RA - Added

    // 03/9/2021 RA - Check the Zip Code validation - passing the country code
    this.setContactZipCodeValidation(this.contactInfoForm.get('countryFormControl').value);
    // 03/09/2021 RA - comment out old code
    // this.contactInfoForm
    //  .get('zipCodeFormControl')
    //  .setValidators([
    //    Validators.required,
    //    Validators.minLength(this.fieldValidations.payment.zipCodeMinLength),
    //    Validators.maxLength(this.fieldValidations.payment.zipCodeMaxLength)
    //  ]);

    // 04/08/2021 RA - Mark Country field as touch to trigger validation of the field to make sure it's valid.
    this.contactInfoForm.get('countryFormControl').markAsTouched();

    this.contactInfoForm
      .get('homePhoneFormControl')
      .setValidators([Validators.required, Validators.pattern(this.fieldValidations.contact.phonePattern)]);
    this.contactInfoForm.get('cellPhoneFormControl').setValidators([Validators.pattern(this.fieldValidations.contact.phonePattern)]);
    this.contactInfoForm.get('workPhoneFormControl').setValidators([Validators.pattern(this.fieldValidations.contact.phonePattern)]);
    this.contactInfoForm
      .get('homeEmailFormControl')
      .setValidators([Validators.required, Validators.pattern(this.fieldValidations.contact.emailAddressPattern)]);

    this.contactInfoForm.get('firstNameFormControl').updateValueAndValidity();
    this.contactInfoForm.get('middleInitialFormControl').updateValueAndValidity();
    this.contactInfoForm.get('lastNameFormControl').updateValueAndValidity();
    this.contactInfoForm.get('addressLine1FormControl').updateValueAndValidity();
    this.contactInfoForm.get('addressLine2FormControl').updateValueAndValidity();
    this.contactInfoForm.get('cityFormControl').updateValueAndValidity();
    this.contactInfoForm.get('countryFormControl').updateValueAndValidity();
    this.contactInfoForm.get('stateFormControl').updateValueAndValidity();
    this.contactInfoForm.get('zipCodeFormControl').updateValueAndValidity();
    this.contactInfoForm.get('homePhoneFormControl').updateValueAndValidity();
    this.contactInfoForm.get('cellPhoneFormControl').updateValueAndValidity();
    this.contactInfoForm.get('workPhoneFormControl').updateValueAndValidity();
    this.contactInfoForm.get('homeEmailFormControl').updateValueAndValidity();

    // 04/06/2021 RA - Verify the State/Country combination to be valid and reload state list with valid states for the loaded country
    this.getStateCountryList(this.contactInfoForm.get('countryFormControl').value);

    this.verifyState_Country(this.contactInfoForm.get('stateFormControl').value,
                             this.contactInfoForm.get('countryFormControl').value);

    // 04/13/2021 RA - To verify if the State/Country combination is valid and if not then mark State as invalid
    if (this.verifySt_CntyListComplete === false) {
      this.contactInfoForm.get('stateFormControl').markAsTouched();
    }

    // 03/09/2021 This will add a value change listener to the country form control -
    //            the code inside will get called with the user changes value for country
    this.contactInfoForm.get('countryFormControl').valueChanges.subscribe(val => {
      this.setContactZipCodeValidation(val);
      this.reset_state(val);
    });

    // 03/11/2021 This will add a value change listener to the zip code form control -
    //            the code inside will get called with the user changes value for zip code
    this.contactInfoForm.get('zipCodeFormControl').valueChanges.subscribe(val => {
      this.setContactZipCodeValidation(this.contactInfoForm.get('countryFormControl').value);
     });

    this.securityInfoForm.get('userNameFormControl')
    .setValidators([Validators.required]);
    this.securityInfoForm
      .get('passwordFormControl')
      .setValidators([Validators.required, Validators.pattern(this.fieldValidations.userRegistration.passwordPattern)]);
    this.securityInfoForm
      .get('passwordConfirmFormControl')
      .setValidators([Validators.required, Validators.pattern(this.fieldValidations.userRegistration.passwordPattern)]);
    this.securityInfoForm.get('userPasswordEqualsFormControl').setValidators([Validators.requiredTrue]);
    this.securityInfoForm.get('securityQuestionFormControl');
    this.securityInfoForm.get('securityQuestionIdFormControl').setValidators([Validators.required]);

    //01/26/2026 RA - Added subscription to security question changes
    this.securityInfoForm.get('securityAnswerFormControl').valueChanges.subscribe(val => {
      this.setSecurityQuestionValidation(val);
    });
    this.securityInfoForm
      .get('securityAnswerFormControl')
      .setValidators([Validators.required,
                      Validators.minLength(this.fieldValidations.contact.securityQuestionAnswerMinLength),
                      Validators.maxLength(this.fieldValidations.contact.securityQuestionAnswerMaxLength),
                      Validators.pattern(this.fieldValidations.userRegistration.securityAnswerPattern)]);

    this.securityInfoForm.get('userNameFormControl').updateValueAndValidity();
    this.securityInfoForm.get('passwordFormControl').updateValueAndValidity();
    this.securityInfoForm.get('passwordConfirmFormControl').updateValueAndValidity();
    this.securityInfoForm.get('securityQuestionFormControl').updateValueAndValidity();
    this.securityInfoForm.get('securityQuestionIdFormControl').updateValueAndValidity();
    this.securityInfoForm.get('securityAnswerFormControl').updateValueAndValidity();

    this.securityInfoForm.get('passwordConfirmFormControl').valueChanges.subscribe(val => {
      this.confirmUserPasswordChanged(val);
    });

    this.securityInfoForm.get('passwordFormControl').valueChanges.subscribe(val => {
      this.confirmUserPasswordChanged(val);
    });
  }

  editContactInformation(): void {
    if (this._userService.isLoggedIn()) {
      this.showContactSaveError = false;
      this.showContactSaveSuccess = false;
      this.allowContactInfoEdit = true;
      this.allowPasswordEdit = false;
      this.allowSecurityQuestionEdit = false;

      // 04/06/2021 RA - To display <mat-error> content for instance when you click the update button and
      //                 validate country/state/zip code data.
      if (this.verifySt_CntyListComplete === false) {
        this.contactInfoForm.get('stateFormControl').markAsTouched();
        this.contactInfoForm.get('stateFormControl').markAsDirty();
        this.contactInfoForm.get('stateFormControl').updateValueAndValidity();

        this.contactInfoForm.get('countryFormControl').markAsTouched();
        this.contactInfoForm.get('countryFormControl').markAsDirty();
        this.contactInfoForm.get('countryFormControl').updateValueAndValidity();
      }

      this.contactInfoForm.get('zipCodeFormControl').markAsTouched();
      this.contactInfoForm.get('zipCodeFormControl').markAsDirty();
      this.contactInfoForm.get('zipCodeFormControl').updateValueAndValidity();
      this.scrollToTop();
    }
  }

  confirmContactInformation(): void {
    this.contactInfoForm.valid;
    this._router.navigate(['']);
  }

  saveContactInformation(): void {
    if (this._userService.isLoggedIn()) {
      this.allowContactInfoEdit = false;
      this.showSaveContactInformationMessageSpinner = true;
      this.showContactSaveError = false;
      this.showContactSaveSuccess = false;

      const contactInformationSend: ContactInformation = {
        participantId: this._userService.getParticipantId(),
        userName: this._userService.getUserName(),
        userId: null,
        firstName: this.contactInfoForm.get('firstNameFormControl').value,
        middleInitial: this.contactInfoForm.get('middleInitialFormControl').value,
        lastName: this.contactInfoForm.get('lastNameFormControl').value,
        addressLine1: this.contactInfoForm.get('addressLine1FormControl').value,
        addressLine2: this.contactInfoForm.get('addressLine2FormControl').value,
        city: this.contactInfoForm.get('cityFormControl').value,
        state: this.contactInfoForm.get('stateFormControl').value,
        zipCode: this.contactInfoForm.get('zipCodeFormControl').value,
        country: this.contactInfoForm.get('countryFormControl').value,
        homePhone: this.contactInfoForm.get('homePhoneFormControl').value,
        cellPhone: this.contactInfoForm.get('cellPhoneFormControl').value,
        workPhone: this.contactInfoForm.get('workPhoneFormControl').value,
        homeEmail: this.contactInfoForm.get('homeEmailFormControl').value,
        addressValid: this.contactInformation.addressValid,
        homePhoneValid: this.contactInformation.homePhoneValid,
        cellPhoneValid: this.contactInformation.cellPhoneValid,
        workPhoneValid: this.contactInformation.workPhoneValid,
        homeEmailValid: this.contactInformation.homeEmailValid
      };

      this._contactService.updateContactInformation(contactInformationSend).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('ContactInfoComponent.saveContactInformation :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('ContactInfoComponent.saveContactInformation :: result = ' + result);
          }

          this.showSaveContactInformationMessageSpinner = false;

          if (Number(result) === 0) {
            contactInformationSend.addressValid = 'VL';
            contactInformationSend.cellPhoneValid = 'VL';
            contactInformationSend.homeEmailValid = 'VL';
            contactInformationSend.homePhoneValid = 'VL';
            contactInformationSend.workPhoneValid = 'VL';
            this.showContactSaveError = false;
            this.showContactSaveSuccess = true;
            this.contactInfoForm.markAllAsTouched();
            this.contactInfoForm.updateValueAndValidity();
          } else {
            this.showContactSaveError = true;
            this.showContactSaveSuccess = false;
          }

          this.contactInformation = contactInformationSend; // Update data for read only view
        },
        error => {
          this._handleError('saveContactInformation', error, contactInformationSend);
          this.showContactSaveError = true;
        }
      );
    }
  }

  // 03/18/2021 RA - Add method to reload the state field filtered by country
  reset_state(newcountry: string) {
    this.contactInfoForm.controls['stateFormControl'].setValue(this.getStateCountryList(newcountry));

    this.contactInfoForm.controls['stateFormControl'].markAsDirty();
    this.contactInfoForm.controls['stateFormControl'].markAsTouched();
    this.contactInfoForm.controls['stateFormControl'].updateValueAndValidity();

    this.contactInfoForm.controls['zipCodeFormControl'].markAsDirty();
    this.contactInfoForm.controls['zipCodeFormControl'].markAsTouched();
    this.contactInfoForm.controls['zipCodeFormControl'].updateValueAndValidity();
  }

  // 03/09/2021 RA - setContactZipCodeValidation will set the validators for contact zip code based on the provided country.
  setContactZipCodeValidation(country: string = null): void {
    const validators: ValidatorFn[] = [
        Validators.minLength(this.getContactZipCodeMinLength(country)),
        Validators.maxLength(this.getContactZipCodeMaxLength(country))
    ];

    // Validators.required(),   -- 03/26/2021 RA Removed to do below
    // 03/25/2021 RA - Add new Zip code required validation only for USA and CAN.
    if (country === 'USA' || country === 'CAN') {
       validators.push(Validators.required);
     } else {
       this.contactInfoForm.get('zipCodeFormControl').clearValidators();
    }

    // 03/10/2021 RA - A valid canadian postcode is 7 digits –
    //   in the format A1A 1A1, where A is a letter and 1 is a digit.
    //   a space separates the third and fourth characters.
    //   do not include the letters D, F, I, O, Q or U.
    //   the first position does not make use of the letters W or Z.
    // 03/25/2021 RA - Added Foreign Regex to be any Alphanumeric or Numeric or Blank
    //
    if (country !== null && country.trim().toUpperCase() === 'CAN') {
      const canadianZipCodeRegex = /^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$/;
      validators.push(Validators.pattern(canadianZipCodeRegex));
    }

    if (country !== null && country.trim().toUpperCase() === 'USA') {
      const usaZipCodeRegex = /^\d{5}(?:[-\s]\d{4})?$/;
      validators.push(Validators.pattern(usaZipCodeRegex));
    }

    if (country !== null && country.trim().toUpperCase() === 'FOR') {
      const forZipCodeRegex = /^([a-zA-Z0-9][\s\\/-]*){0,10}$/;
      validators.push(Validators.pattern(forZipCodeRegex));
    }
    this.contactInfoForm.controls['zipCodeFormControl'].markAsDirty();
    this.contactInfoForm.get('zipCodeFormControl').setValidators(validators);
    this.contactInfoForm.get('zipCodeFormControl').updateValueAndValidity();
  }

  // 03/09/2021 RA - contactZipCodeOnlyAllowsNumbers checks to see if zip code entry allows numbers (used in the HTML file)
  contactZipCodeOnlyAllowsNumbers(): boolean {
    if (this.contactInfoForm.get('countryFormControl').value !== null
      && String(this.contactInfoForm.get('countryFormControl').value).toUpperCase() !== 'USA') {
        return false;
    } else {
      return true;
    }
  }

  // 03/09/2021 RA - getContactZipCodeMinLength returns the min zip code length based on country
  // 03/25/2021 RA - Broke out Foreign into its own category
  getContactZipCodeMinLength(country: string = null): number {
    if (country !== null) {
      if (country.trim().toUpperCase() === 'USA') {
        return this.fieldValidations.contact.zipCodeMinLength_USA;
      } else if (country.trim().toUpperCase() === 'CAN') {
        return this.fieldValidations.contact.zipCodeMinLength_CAN;
      } else if (country.trim().toUpperCase() === 'FOR') {
        return this.fieldValidations.contact.zipCodeMinLength_FOR;
      } else {
        return this.fieldValidations.contact.zipCodeMinLength;
      }
    } else {
      return this.fieldValidations.contact.zipCodeMinLength;
    }
  }

  // 03/09/2021 RA - getContactZipCodeMaxLength returns the max zip code length based on country
  // 03/25/2021 RA - Broke out Foreign into its own category
  getContactZipCodeMaxLength(country: string = null): number {
    if (country !== null) {
      if (country.trim().toUpperCase() === 'USA') {
        return this.fieldValidations.contact.zipCodeMaxLength_USA;
      } else if (country.trim().toUpperCase() === 'CAN') {
        return this.fieldValidations.contact.zipCodeMaxLength_CAN;
      } else if (country.trim().toUpperCase() === 'FOR') {
        return this.fieldValidations.contact.zipCodeMaxLength_FOR;
      } else {
        return this.fieldValidations.contact.zipCodeMaxLength;
      }
    } else {
      return this.fieldValidations.contact.zipCodeMaxLength;
    }
  }

  editPassword(): void {
    if (this._userService.isLoggedIn()) {
      this.showPasswordSaveError = false;
      this.showPasswordSaveSuccess = false;
      this.allowContactInfoEdit = false;
      this.allowPasswordEdit = true;
      this.allowSecurityQuestionEdit = false;
    }
  }

  confirmUserPasswordChanged(newConfirmUserPassword: string) {
    this.securityInfoForm.controls['userPasswordEqualsFormControl'].setValue(this.areUserPasswordsEqual());
    this.securityInfoForm.controls['userPasswordEqualsFormControl'].markAsDirty();
    this.securityInfoForm.controls['userPasswordEqualsFormControl'].markAsTouched();
    this.securityInfoForm.get('userPasswordEqualsFormControl').updateValueAndValidity();
  }

  areUserPasswordsEqual(): boolean {
    return (
      this.securityInfoForm.controls['passwordConfirmFormControl'].value === this.securityInfoForm.controls['passwordFormControl'].value
    );
  }

  savePassword(): void {
    if (this._userService.isLoggedIn()) {
      this.allowPasswordEdit = false;
      this.showSavePasswordMessageSpinner = true;
      this.showPasswordSaveError = false;
      this.showPasswordSaveSuccess = false;

      const userNamePasswordChange: UserNamePasswordChange = {
        oldUserName: this.securityInfoForm.get('userNameFormControl').value,
        newUserName: null,
        password: this.securityInfoForm.get('passwordFormControl').value,
        participantId: this._userService.getParticipantId(),
        companyCode: this._companyService.getCompanyCode()                             // 10/22/2021 RA
      };

      this._contactService.updatePassword(userNamePasswordChange).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('ContactInfoComponent.savePassword :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('ContactInfoComponent.savePassword :: result = ' + result);
          }

          this.showSavePasswordMessageSpinner = false;

          if (Number(result) === 0) {
            this.showPasswordSaveError = false;
            this.showPasswordSaveSuccess = true;
          } else {
            this.showPasswordSaveError = true;
            this.showPasswordSaveSuccess = false;
          }
        },
        error => {
          this._handleError('savePassword', error, userNamePasswordChange);
          this.showContactSaveError = true;
        }
      );
    }
  }

  editSecurityQuestion(): void {
    if (this._userService.isLoggedIn()) {
      this.showPasswordSaveError = false;
      this.showPasswordSaveSuccess = false;
      this.allowContactInfoEdit = false;
      this.allowPasswordEdit = false;
      this.allowSecurityQuestionEdit = true;
    }
  }

  // 01/26/2026 RA - setSecurityQuestionValidation will set the validators for security question.
  setSecurityQuestionValidation(securityAnswer: string = null): void {
    const validators: ValidatorFn[] = [
        Validators.minLength(this.getSecurityAnswerMinLength(securityAnswer)),
        Validators.maxLength(this.getSecurityAnswerMaxLength(securityAnswer))
    ];

    // 03/25/2021 RA - Added Foreign Regex to be any Alphanumeric or Numeric or Blank
    //
    if (securityAnswer !== null) {
      const securityAnswerRegex = /^[a-zA-Z0-9]+$/;
      validators.push(Validators.pattern(securityAnswerRegex));
    }

    this.securityInfoForm.controls['securityAnswerFormControl'].markAsDirty();
    this.securityInfoForm.get('securityAnswerFormControl').setValidators(validators);
    this.securityInfoForm.get('securityAnswerFormControl').updateValueAndValidity();
  }

  // 01/26/2026 RA - Check min length for security answer
  getSecurityAnswerMinLength(securityAnswer: string = null): number {
    if (securityAnswer !== null) {
      return this.fieldValidations.contact.securityQuestionAnswerMinLength;
    }
  }

  // 01/26/2026 RA - Check max length for security answer
  getSecurityAnswerMaxLength(securityAnswer: string = null): number {
    if (securityAnswer !== null) {
      return this.fieldValidations.contact.securityQuestionAnswerMaxLength;
    }
  }

  saveSecurityQuestion(): void {
    if (this._userService.isLoggedIn()) {
      this.allowSecurityQuestionEdit = false;
      this.showSaveSecurityQuestionMessageSpinner = true;
      this.showSecurityQuestionSaveError = false;
      this.showSecurityQuestionSaveSuccess = false;

      const securityQuestionChange: SecurityQuestionChange = {
        userName: this.securityInfoForm.get('userNameFormControl').value,
        userId: this.userId,
        securityQuestionId: this.securityInfoForm.get('securityQuestionIdFormControl').value,
        securityQuestionText: this.getSecurityQuestionText(),
        securityQuestionAnswer: this.securityInfoForm.get('securityAnswerFormControl').value,
        participantId: this._userService.getParticipantId()
      };

      this._contactService.updateSecurityQuestionLogged(securityQuestionChange).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('ContactInfoComponent.saveSecurityQuestion :: result = ' + result);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('ContactInfoComponent.saveSecurityQuestion :: result = ' + result);
          }

          this.showSaveSecurityQuestionMessageSpinner = false;

          if (Number(result) === 0) {
            this.showSecurityQuestionSaveError = false;
            this.showSecurityQuestionSaveSuccess = true;
          } else {
            this.showSecurityQuestionSaveError = true;
            this.showSecurityQuestionSaveSuccess = false;
          }
        },
        error => {
          this._handleError('saveSecurityQuestion', error, securityQuestionChange);
          this.showContactSaveError = true;
        }
      );
    }
  }

  getSecurityQuestionText(): string {
    const selectedSecurityQuestionsList = this.securityQuestionsList.filter(
      x => x.securityQuestionId === Number(this.securityInfoForm.get('securityQuestionIdFormControl').value)
    );
    return selectedSecurityQuestionsList !== null && selectedSecurityQuestionsList.length > 0
      ? selectedSecurityQuestionsList[0].securityQuestion
      : null;
  }

  saveContactInformationButtonEnabled(): boolean {
    return this.contactInfoForm.valid;
  }

  savePasswordButtonEnabled(): boolean {
    return (
      this.securityInfoForm.get('passwordFormControl').valid &&
      this.securityInfoForm.get('passwordConfirmFormControl').valid &&
      this.securityInfoForm.get('userPasswordEqualsFormControl').valid
    );
  }

  saveSecurityQuestionButtonEnabled(): boolean {
    return this.securityInfoForm.get('securityQuestionIdFormControl').valid && this.securityInfoForm.get('securityAnswerFormControl').valid;
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }
}
